import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Zap, Target, Shield, Users, Sparkles } from 'lucide-react';
import bg from '../images/best.jpg';

export default function About() {
  const glassEffect = 'bg-black/30 backdrop-blur-xl border border-gray-800/40';

  const coreFeatures = [
    {
      icon: '📊',
      title: "Mind Maps & Diagrams",
      description: "Transform complex concepts into visual mind maps and diagrams that make studying intuitive and engaging."
    },
    {
      icon: '🎴',
      title: "AI-Powered Flashcards",
      description: "Instantly convert your notes or PDFs into smart flashcards with AI-generated questions for effective revision."
    },
    {
      icon: '📝',
      title: "Question Paper Generator",
      description: "Teachers can generate customized question papers in .DOCX or .PDF format with adjustable difficulty levels."
    },
    {
      icon: '🌍',
      title: "Smart Summaries",
      description: "Get crystal-clear summaries that capture the essence of your content, saving hours of study time."
    },
    {
      icon: '🤔',
      title: "Doubt Generation",
      description: "Anticipate questions before they arise with AI-generated doubts that deepen your understanding."
    },
    {
      icon: '📖',
      title: "Vocabulary Builder",
      description: "Extract and learn important words from your content to enhance your vocabulary effortlessly."
    },
    {
      icon: '🥇',
      title: "Rewards & Progress Tracking",
      description: "Earn bronze, silver, and gold medals for correctly answered questions while tracking your learning journey."
    },
    {
      icon: '✅',
      title: "Grammar Correction",
      description: "Perfect your writing with advanced grammar corrections powered by AI."
    },
    {
      icon: '💭',
      title: "Snapshot Generation",
      description: "Get detailed snapshots of any topic you enter for quick reference and revision."
    },
    {
      icon: '🤩',
      title: "Study Plan Generator",
      description: "Receive detailed, personalized study plans based on your content and learning goals."
    },
    {
      icon: '📌',
      title: "Knowledge Graphs",
      description: "Visualize connections between concepts with interactive knowledge graphs."
    },
    {
      icon: '🙎‍♂️',
      title: "Text Humanizer",
      description: "Make AI-generated content more natural and human-like with our humanization tool."
    }
  ];

  const stats = [
    { number: "10K+", label: "Active Users" },
    { number: "100+", label: "Countries" },
    { number: "#2", label: "Top Research Startup" },
    { number: "Free", label: "Forever" }
  ];

  const whyDifferent = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Built by Students, For Students",
      description: "NexNotes AI isn't just another AI tool - we're a personalized study assistant that understands the real challenges students face."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Faster Than Competitors",
      description: "Our platform is optimized for speed, delivering results in seconds while other tools make you wait."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "All-in-One Solution",
      description: "Why juggle multiple tools? From diagrams to handwritten notes, question papers to quizzes - everything you need is here."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Privacy First",
      description: "We DO NOT store your data. Your documents are processed securely and never saved on our servers."
    }
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Image */}
      <div 
        className="lumin-landing-bg" 
        style={{
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      ></div>

      {/* Hero Section */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 mb-12 text-center`}>
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center mb-6">
            <Sparkles className="w-16 h-16 text-blue-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            About NexNotes AI
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-6">
            Your personalized AI study buddy that transforms how you learn. From notes to question papers, mind maps to flashcards - all in seconds, completely free.
          </p>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold">
            <Award className="w-5 h-5" />
            Ranked #2 in Top Research Startups by F6S
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 mb-12`}>
        <h2 className="text-3xl font-bold mb-8 text-center">Trusted Worldwide</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
                {stat.number}
              </div>
              <div className="text-gray-400 text-sm sm:text-base">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Mission Statement */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 mb-12`}>
        <h2 className="text-3xl font-bold mb-6 text-center">Our Mission</h2>
        <p className="text-lg text-gray-300 leading-relaxed max-w-4xl mx-auto text-center mb-4">
          At NexNotes AI, we believe studying should be efficient, not exhausting. Our mission is to eliminate repetitive, time-consuming tasks that hold students back from actual learning.
        </p>
        <p className="text-lg text-gray-300 leading-relaxed max-w-4xl mx-auto text-center">
          We're not just another AI tool - we're building a personalized study assistant that understands your needs, tracks your progress, and helps you gain 2x more knowledge in the same amount of time.
        </p>
      </div>

      {/* Core Features */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">Everything You Need to Excel</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreFeatures.map((feature, index) => (
            <div
              key={index}
              className={`${glassEffect} rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 group`}
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* What Makes Us Different */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">What Makes Us Different</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {whyDifferent.map((item, index) => (
            <div
              key={index}
              className={`${glassEffect} rounded-2xl p-6 sm:p-8 hover:border-purple-500/50 transition-all duration-300 group`}
            >
              <div className="text-purple-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
              <p className="text-gray-400 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 mb-12`}>
        <h2 className="text-3xl font-bold mb-6 text-center">Why Students Love NexNotes AI</h2>
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-start gap-4">
            <span className="text-blue-400 text-2xl">⏱️</span>
            <div>
              <h3 className="text-xl font-semibold mb-2">Save 5+ Hours Per Week</h3>
              <p className="text-gray-300">No more manual note-taking, question paper creation, or flashcard making. Let AI handle the repetitive work.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="text-purple-400 text-2xl">⚡</span>
            <div>
              <h3 className="text-xl font-semibold mb-2">Learn 2x Faster</h3>
              <p className="text-gray-300">Gain twice the knowledge in the same amount of time with AI-optimized study materials.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="text-pink-400 text-2xl">🎯</span>
            <div>
              <h3 className="text-xl font-semibold mb-2">For Every Learning Style</h3>
              <p className="text-gray-300">Whether you're a visual learner who loves mind maps or prefer flashcards - we've got you covered.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="text-green-400 text-2xl">👨‍🏫</span>
            <div>
              <h3 className="text-xl font-semibold mb-2">Not Just for Students</h3>
              <p className="text-gray-300">Teachers can generate customized question papers instantly, saving hours of preparation time.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Founder Section */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 mb-12`}>
        <div className="flex justify-center mb-6">
          <Users className="w-12 h-12 text-purple-400" />
        </div>
        <h2 className="text-3xl font-bold mb-6 text-center">Meet the Founder</h2>
        <p className="text-lg text-gray-300 leading-relaxed max-w-4xl mx-auto text-center mb-4">
          NexNotes AI was founded by <span className="font-semibold text-white">Arush Sharma</span>, a student who experienced firsthand the pain of repetitive, time-consuming study tasks. As the sole founder, developer, and marketer, Arush built NexNotes AI with one goal: to help students study smarter, not harder.
        </p>
        <p className="text-lg text-gray-300 leading-relaxed max-w-4xl mx-auto text-center">
          What started as a personal project to solve a student's problem has grown into a platform trusted by over 10,000 users across 100+ countries.
        </p>
      </div>

      {/* Commitment Section */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 mb-12`}>
        <h2 className="text-3xl font-bold mb-6 text-center">Our Commitment to You</h2>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2 text-blue-400">Free Forever (or Affordable)</h3>
            <p className="text-gray-300">Our aim is to keep this tool free or at least affordable for students. Education shouldn't break the bank.</p>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2 text-purple-400">Privacy First</h3>
            <p className="text-gray-300">We absolutely DO NOT store your data. Your privacy is our top priority. Check our privacy policy for complete transparency.</p>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2 text-pink-400">Continuous Innovation</h3>
            <p className="text-gray-300">We're constantly adding new features based on student feedback. Big updates are always loading!</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className={`${glassEffect} rounded-3xl p-8 sm:p-12 text-center`}>
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Study Routine?</h2>
        <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
          Join 10,000+ students worldwide who are already studying smarter with NexNotes AI.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/app"
            className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg"
          >
            Start Learning Smarter →
          </Link>
          <Link
            to="/pricing"
            className="px-8 py-3 bg-white/10 backdrop-blur-sm border border-gray-700/50 rounded-full font-semibold hover:bg-white/20 transition-all duration-200"
          >
            View Pricing
          </Link>
        </div>
        <p className="text-sm text-gray-400 mt-6">
          Questions? Contact: arush2022pro@gmail.com
        </p>
      </div>
    </div>
  );
}